import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DogImage from "@/components/DogImage";
import DonationQR from "@/components/DonationQR";
import PayPalHostedButton from "@/components/PayPalHostedButton";
import { Heart, ExternalLink, ArrowRight } from "lucide-react";
import { ZEFFY_MAIN, ZEFFY_SPIKE, PAYPAL_DONATE, VENMO_URL, CONTACT } from "@/constants/config";
import { Link } from "react-router-dom";
import PageMeta from "@/components/PageMeta";

export default function Donate() {
  const [dogs, setDogs] = useState([]);
  useEffect(() => { api.get("/dogs").then((r) => setDogs(r.data || [])).catch(() => {}); }, []);

  const featured = dogs.find((d) => d.featured);

  return (
    <div>
      <PageMeta
        title="Donate — Support Bulldog Rescue via Zeffy, PayPal, Venmo | Lavish 808 Bulliez"
        description="Donate to help rescue bulldogs and fund life-saving vet care. Give via Zeffy (0% fees), PayPal, or Venmo — 100% of your gift supports pups from Hawaiʻi to Murrieta, California."
      />
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>Kōkua • Give</div>
          <h1 className="font-serif text-5xl md:text-7xl mb-6" style={{ color: "#0E5C6E" }}>Give with Aloha.</h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            100% of your donation reaches our dogs. Give via <strong>Zeffy</strong> — built for
            nonprofits with <strong>zero platform fees</strong> — or <strong>PayPal</strong> if you prefer.
            Every dollar goes to vet care, transport from Hawaiʻi, food, and safe fosters.
          </p>
        </div>
      </section>

      {/* Main Zeffy CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center p-10 md:p-16 rounded-3xl" style={{ backgroundColor: "#0E5C6E" }}>
          <div className="text-xs uppercase tracking-[0.3em] mb-4 text-white/70">Support our mission</div>
          <h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Donate to change lives.</h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">
            One-time or recurring — every gift funds another rescue. You choose the amount.
          </p>
          <div className="flex flex-col items-center justify-center gap-5">
            <a href={ZEFFY_MAIN} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="donate-zeffy-main">
              <Heart size={18} fill="white" /> Donate via Zeffy <ExternalLink size={14} />
            </a>
            <div className="text-xs uppercase tracking-[0.3em] text-white/50">— or —</div>
            <PayPalHostedButton testId="donate-paypal-main" />
            <a
              href={PAYPAL_DONATE}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs underline text-white/70 hover:text-white transition-colors"
              data-testid="donate-paypal-link"
            >
              Prefer to donate on PayPal.com? Open PayPal →
            </a>
            <a
              href={VENMO_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              style={{ backgroundColor: "#008CFF", color: "white" }}
              data-testid="donate-venmo-link"
            >
              Send on Venmo @lavish808buliiezrescuedpaws
            </a>
          </div>
          <div className="text-xs text-white/60 mt-6">
            <strong className="text-white/80">Zeffy</strong> has 0% platform fees — every dollar reaches our dogs.
            <span className="mx-2">•</span>
            <strong className="text-white/80">PayPal / Venmo</strong> — familiar and fast, small processing fee applies.
          </div>
        </div>
      </section>

      {/* Featured campaign */}
      {featured && (
        <section className="py-16" style={{ backgroundColor: "#F7ECD9" }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-5">
              <div className="rounded-3xl overflow-hidden aspect-[4/5]">
                <DogImage dog={featured} />
              </div>
            </div>
            <div className="md:col-span-7">
              <div className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: "#E27E4E" }}>Urgent Campaign • Mālama {featured.name}</div>
              <h2 className="font-serif text-3xl md:text-4xl mb-4" style={{ color: "#0E5C6E" }}>
                Fund {featured.name}'s life-changing surgery.
              </h2>
              <p className="mb-8 leading-relaxed" style={{ color: "#2D2B2A" }}>{featured.story}</p>
              <div className="flex flex-wrap gap-3">
                <a href={featured.zeffy_url || ZEFFY_SPIKE} target="_blank" rel="noopener noreferrer" className="btn-donate" data-testid="donate-zeffy-spike">
                  <Heart size={18} fill="white" /> Give to {featured.name} <ExternalLink size={14} />
                </a>
                <Link
                  to="/qr-flyer/spike"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-medium border transition-colors"
                  style={{ borderColor: "#0E5C6E", color: "#0E5C6E", backgroundColor: "white" }}
                  data-testid="donate-spike-flyer-link"
                >
                  Print {featured.name}'s flyer
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other ways */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-3 text-center" style={{ color: "#E27E4E" }}>Other ways to help</div>
          <h2 className="font-serif text-3xl md:text-4xl mb-12 text-center" style={{ color: "#0E5C6E" }}>Every act of Aloha counts.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <WayCard title="Sponsor a pup" desc="Kōkua a dog every month, from $10." link="/sponsor" cta="Start sponsoring" />
            <WayCard title="Adopt" desc="Give a bulldog their forever ʻohana." link="/adopt" cta="Meet our dogs" />
            <WayCard title="Foster / Volunteer" desc="Open your home, or lend a hand." link="/get-involved" cta="Get involved" />
            <WayCard title="Spread the word" desc="Share our story on social — free, and it saves lives." link={CONTACT.instagram} external cta="Follow on Instagram" />
          </div>
        </div>
      </section>

      {/* QR Code — scan to donate */}
      <DonationQR />

      {/* Trust note */}
      <section className="py-14 text-center" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm" style={{ color: "#6B655E" }}>
            {CONTACT.org} is a registered <strong>501(c)(3)</strong> nonprofit • EIN {CONTACT.ein}.
            All donations are tax-deductible to the extent permitted by law.
          </p>
        </div>
      </section>
    </div>
  );
}

function WayCard({ title, desc, link, cta, external }) {
  const Content = (
    <>
      <h3 className="font-serif text-2xl mb-2" style={{ color: "#0E5C6E" }}>{title}</h3>
      <p className="text-sm mb-4" style={{ color: "#2D2B2A" }}>{desc}</p>
      <div className="text-xs uppercase tracking-[0.15em] flex items-center gap-2" style={{ color: "#E27E4E" }}>
        {cta} <ArrowRight size={12} />
      </div>
    </>
  );
  const cls = "card-dog p-8 block";
  return external
    ? <a href={link} target="_blank" rel="noopener noreferrer" className={cls}>{Content}</a>
    : <Link to={link} className={cls}>{Content}</Link>;
}
